import { Divider, Stack, styled } from '@mui/material'
import { Fragment, useCallback } from 'react'
import { SettingsSection } from '$common/settings-section'
import { ListItemCriticalTextButton } from '$common/buttons'
import { showRemoveMenu } from '$comp/modals/menus/remove-menu'
import { showTerminateSessionsModal } from '$comp/modals/confirmation-dialog'
import { useActiveSessions } from '$logic/auth/react-hook'
import { SessionItem } from './session-item'
import { t } from '$i18n'

const InfixDivider = styled(Divider)`
  &:last-child {
    display: none;
  }
`

interface SessionsListProps {
  showCurrentDevice?: boolean
  onTerminateOtherSessions?: () => void
  onTerminateSession?: (selector: string) => void
}

export const SessionsList = ({
  showCurrentDevice = true,
  onTerminateOtherSessions,
  onTerminateSession,
}: SessionsListProps) => {
  const {
    value: activeSessions,
    loading = false,
    terminateOtherSessions,
    terminateSession,
  } = useActiveSessions()

  const { current, others } = activeSessions || {}

  const handleTerminateOtherSessions = useCallback(() => {
    showTerminateSessionsModal(() =>
      terminateOtherSessions().then(() => {
        if (onTerminateOtherSessions) {
          onTerminateOtherSessions()
        }
      }),
    )
  }, [terminateOtherSessions, onTerminateOtherSessions])

  const handleTerminateSession = useCallback(
    (selector: string) => {
      showRemoveMenu({
        onRemove: () =>
          terminateSession(selector).then(() => {
            if (onTerminateSession) {
              onTerminateSession(selector)
            }
          }),
      })
    },
    [terminateSession, onTerminateSession],
  )

  if (loading || !activeSessions) {
    return null // TODO: Implement skeleton
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {showCurrentDevice && current && (
        <SettingsSection headerText={t('activeSessions.currentDevice')}>
          <>
            <SessionItem
              ip={current.identity?.ip}
              userAgent={current.identity?.userAgent}
              date={current.updatedAt}
            />
            <Divider />
            <ListItemCriticalTextButton
              text={t('activeSessions.deactivateOtherDevices')}
              onClick={handleTerminateOtherSessions}
            />
          </>
        </SettingsSection>
      )}

      {others && others.length > 0 && (
        <SettingsSection headerText={t('activeSessions.otherActiveDevices')}>
          <>
            {others.map(({ updatedAt, identity, selector }) => (
              <Fragment key={selector}>
                <SessionItem
                  ip={identity?.ip}
                  userAgent={identity?.userAgent}
                  date={updatedAt}
                  onClick={() => handleTerminateSession(selector)}
                />
                <InfixDivider />
              </Fragment>
            ))}
          </>
        </SettingsSection>
      )}
    </Stack>
  )
}
